import React from 'react';
import { CircleQuestionIcon } from '@revfluence/fresh-icons/solid/esm';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Input } from '@frontend/shadcn/components/ui/input';
import { XmarkIcon, CheckIcon } from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { PenIcon } from '@revfluence/fresh-icons/regular';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import Tag from '@frontend/app/refresh-components/Tag';
import { OFFER_PAYOUT_TYPE } from '../../../types/globalTypes';
import { getPayoutTag, getPayoutTagInitials, getUniqueColorForPayout } from '../../OfferForm/utils/OfferFormUtils';
import { PayoutState } from './types';

export const PayoutHeader: React.FC<{
  isEditingTitle: boolean;
  setIsEditingTitle: (value: boolean) => void;
  payoutState: PayoutState;
  updatePayoutState: <K extends keyof PayoutState>(key: K, value: PayoutState[K]) => void;
  formattedConversionType: string;
  isRuleEditing: boolean;
}> = ({ isEditingTitle, setIsEditingTitle, payoutState, updatePayoutState, formattedConversionType, isRuleEditing }) => {
  return (
    <Card className="bg-grey-1 border border-grey-2 px-4 py-2 flex justify-between items-center h-12 rounded-none">
      <div className="flex items-center gap-2">
        {isEditingTitle ? (
          <>
            <Input
              type="text"
              maxLength={60}
              placeholder="Payout Title"
              className="w-full max-w-[229px] bg-primary-foreground transition-all duration-300 ease-in-out"
              value={payoutState.tempPayoutTitles}
              onChange={(e) => updatePayoutState('tempPayoutTitles', e.target.value)}
            />
            <Button
              variant="outline"
              size="smallIcon"
              type="button"
              onClick={() => setIsEditingTitle(false)}
              className="cursor-pointer transition-all duration-300 ease-in-out hover:bg-grey-1 hover:border-grey-5 hover:text-grey-5 w-8"
            >
              <XmarkIcon />
            </Button>
            <Button
              size="smallIcon"
              type="button"
              onClick={() => setIsEditingTitle(false)}
              disabled={payoutState.tempPayoutTitles === ''}
              className="cursor-pointer transition-all duration-300 ease-in-out w-8"
            >
              <CheckIcon />
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm font-medium m-0 text-primary">{payoutState.tempPayoutTitles}</p>
            <Button
              variant="outline"
              size="smallIcon"
              type="button"
              disabled={isRuleEditing}
              onClick={() => setIsEditingTitle(true)}
              aria-label="pen"
              className="cursor-pointer transition-all duration-300 ease-in-out hover:border hover:text-grey-5 border-0 bg-grey-1"
            >
              <PenIcon fontSize={12} />
            </Button>
          </>
        )}
      </div>
      <div className=" flex items-center gap-1 px-2">
        {isEditingTitle ? (
          <Select
            value={payoutState.tempPayoutType}
            onValueChange={(value: OFFER_PAYOUT_TYPE) => updatePayoutState('tempPayoutType', value)}
          >
            <SelectTrigger
              className={`w-max border-0 font-normal text-primary focus:outline-none focus:ring-0 ${getUniqueColorForPayout(
                payoutState.tempPayoutType,
              )} h-6 px-2`}
            >
              <SelectValue placeholder="Select Payout Type">
                {getPayoutTagInitials(payoutState.tempPayoutType)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="z-[9999]">
              <SelectItem value={OFFER_PAYOUT_TYPE.CONVERSION}>
                {getPayoutTag(OFFER_PAYOUT_TYPE.CONVERSION, formattedConversionType)}
              </SelectItem>
              <SelectItem value={OFFER_PAYOUT_TYPE.SALE}>
                {getPayoutTag(OFFER_PAYOUT_TYPE.SALE, formattedConversionType)}
              </SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Tag
            bgColor={getUniqueColorForPayout(payoutState.tempPayoutType)}
            label={getPayoutTagInitials(payoutState.tempPayoutType)}
            textColor="text-primary"
          />
        )}
      </div>
      <div className="flex items-center gap-2">
        <p className="m-0 text-xs flex items-center font-medium text-nowrap">
          <CircleQuestionIcon fontSize={14} className="text-grey-5 mr-2" />
          <a href="https://help.aspireiq.com/en/articles/10895521-beta-commission-payout-rules" target="_blank" rel="noopener noreferrer" className="text-primary">
            Need Help ?
          </a>
        </p>
      </div>
    </Card>
  );
};
